import { cloneDeep } from "lodash";
import {
  Dispatch,
  SetStateAction,
  createContext,
  createElement,
  useState,
} from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router";
import { Wizard } from "../../../../components";
import { useAxios, useSearchAddress } from "../../../../hooks";
import { Address, Company } from "../../../../types";
import AddressTab from "./Address";
import AdminInfo from "./AdminInfo";
import Finalize from "./Finalize";
import Information from "./Information";
import { ValidateAddress } from "../../../../methods";

type dataType = Company.Create;
type contextType = {
  data: dataType;
  setData: Dispatch<SetStateAction<dataType>>;
  handleSetValue: (key: keyof dataType) => (value: any) => void;
  handleSetAddressValue: (key: keyof Address) => (value: any) => void;
  addressErrorMsg: string;
};

export const Context = createContext({} as contextType);

export default function CreateCompany() {
  const steps = [
    {
      label: "b2bManagement.companies.create.companyInformationTab",
      text: "b2bManagement.companies.create.companyInformationDesc",
      id: 0,
      component: Information,
    },
    {
      label: "b2bManagement.companies.create.addressInformationTab",
      text: "b2bManagement.companies.create.addressInformationDesc",
      id: 1,
      component: AddressTab,
    },
    {
      label: "b2bManagement.companies.create.adminInfoTab",
      text: "b2bManagement.companies.create.adminInfoDesc",
      id: 2,
      component: AdminInfo,
    },
    {
      label: "b2bManagement.companies.create.finalizeTab",
      text: "b2bManagement.companies.create.finalizeDesc",
      id: 3,
      component: Finalize,
    },
  ];
  const navigate = useNavigate();
  const { axios, loading } = useAxios();
  const [activeIndex, setActiveIndex] = useState(0);
  const [data, setData] = useState({} as dataType);
  const isFirstStep = activeIndex === 0;
  const isLastStep = activeIndex === steps.length - 1;
  const isLocationStep = activeIndex === 1;
  const [addressErrorMsg, setAddressErrorMasg] = useState("");
  const searchAddress = useSearchAddress();
  const [validatingAddress, setValidatingAddress] = useState(false);

  const handleSetValue = (key: keyof dataType) => {
    return (value: any) =>
      setData((p) => {
        const data = cloneDeep(p);
        data[key] = value;
        return data;
      });
  };

  const handleSetAddressValue = (key: keyof Address) => {
    return (value: any) =>
      setData((p) => {
        const data = cloneDeep(p);
        data.address ??= {} as Address;
        data.address[key] = value;
        return data;
      });
  };

  const handleCancel = () => {
    if (isFirstStep) return navigate(-1);
    setActiveIndex((p) => p - 1);
  };

  const validateAddress = async () => {
    setAddressErrorMasg("");
    setValidatingAddress(true);
    await ValidateAddress(data.address as any, searchAddress).then(res => {
      if (!res?.selected && !res.suggested) {
        setAddressErrorMasg("Address is not valid");
      }
      if (res.selected) {
        setData({
          ...data,
          address: {
            ...res.selected,
            name: data.address.name,
            number: data.address.number,
          },
        });
        setActiveIndex((p) => p + 1)
      }
      if (res.suggested) {
        setData({
          ...data,
          address: {
            ...res.suggested,
            name: data.address.name,
            number: data.address.number,
          },
        });
        setAddressErrorMasg(
          "Address has been changed please check before submitting",
        );
      }
    }).finally(() => {
      setValidatingAddress(false);
    });
  }

  const submit = async () => {
    if (isLocationStep && data.address) { await validateAddress() }
    else if (isLastStep) {
      const url = "/accountservice/api/companies";
      const body = { ...data };
      axios.post(url, body).then(({ data: id }) => {
        const url = `/companies/${id}`;
        const message = "b2bManagement.companies.create.successMessage";
        toast.success(message);
        navigate(url);
      });
    }
    else setActiveIndex((p) => p + 1);
  };

  return (
    <Wizard onSubmit={submit} onCancel={handleCancel} activeStep={activeIndex}>
      <Wizard.Items>
        {steps.map((e) => (
          <Wizard.Item
            key={e.id}
            index={e.id}
            label={e.label}
            description={e.text}
          />
        ))}
      </Wizard.Items>
      <Wizard.Tabs>
        <Context.Provider
          value={{ data, setData, handleSetValue, handleSetAddressValue, addressErrorMsg }}
        >
          {steps.map((e) => (
            <Wizard.Tab
              key={e.id}
              index={e.id}
              label={e.label}
              loading={loading.post || validatingAddress}
              isLastStep={isLastStep}
            >
              {createElement(e.component)}
            </Wizard.Tab>
          ))}
        </Context.Provider>
      </Wizard.Tabs>
    </Wizard>
  );
}
